import cx from "classnames";
import React, { useId, useRef, useState } from "react";

import { Dropdown, Icon, IconSize } from "@gaming/xds-react-js";
import { Close } from "@gaming/xds-react-js-icons";

import * as styles from "../../AdminPage.module.css";

type AdminDropdownOption<T extends string = string> = Readonly<{
  id: T;
  label: string;
  icon?: React.ReactNode;
}>;

type AdminDropdownShellProps = Readonly<{
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  displayLabel: string;
  renderOptions: (close: () => void) => React.ReactNode;
  triggerRole?: "combobox";
}>;

type AdminSelectDropdownProps<T extends string> = Readonly<{
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  onChange: (value: T) => void;
  options: AdminDropdownOption<T>[];
  value: T;
}>;

type AdminMultiSelectDropdownProps = Readonly<{
  ariaLabel: string;
  className?: string;
  /** Shows a footer button that clears the selection. */
  clearLabel?: string;
  /** Overrides the default "label (count)" trigger text. */
  displayLabel?: string;
  label: string;
  onChange: (values: string[]) => void;
  options: AdminDropdownOption[];
  values: string[];
}>;

const AdminDropdownShell = ({
  ariaLabel,
  className,
  disabled,
  displayLabel,
  renderOptions,
  triggerRole,
}: AdminDropdownShellProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const close = () => setIsOpen(false);

  return (
    <div className={cx(styles.adminDropdown, className)}>
      <button
        ref={triggerRef}
        type="button"
        role={triggerRole}
        className={styles.adminDropdownTrigger}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-controls={menuId}
        aria-haspopup={triggerRole ? "listbox" : "menu"}
        disabled={disabled}
        onClick={() => setIsOpen(current => !current)}
      >
        <span>{displayLabel}</span>
        <span className={styles.adminDropdownChevron} aria-hidden="true" />
      </button>
      {isOpen && (
        <Dropdown
          isOpen={isOpen}
          onClose={close}
          relativeRef={triggerRef as React.RefObject<HTMLElement>}
          backgroundDismissable
          dimBackground="none"
          className={styles.adminDropdownMenu}
        >
          <ul id={menuId} className={styles.adminDropdownList} aria-label={ariaLabel}>
            {renderOptions(close)}
          </ul>
        </Dropdown>
      )}
    </div>
  );
};

export const AdminSelectDropdown = <T extends string>({
  ariaLabel,
  className,
  disabled,
  label,
  onChange,
  options,
  value,
}: AdminSelectDropdownProps<T>) => {
  const selectedOption = options.find(option => option.id === value);
  const displayLabel = [label, selectedOption?.label].filter(Boolean).join(": ");

  return (
    <AdminDropdownShell
      ariaLabel={ariaLabel}
      className={className}
      disabled={disabled}
      displayLabel={displayLabel}
      triggerRole="combobox"
      renderOptions={close =>
        options.map(option => {
          const selected = option.id === value;
          return (
            <li key={option.id}>
              <button
                type="button"
                aria-pressed={selected}
                className={styles.adminDropdownOption}
                onClick={() => {
                  onChange(option.id);
                  close();
                }}
              >
                <span>{option.label}</span>
                {selected && <span className={styles.adminDropdownCheckmark} aria-hidden="true" />}
              </button>
            </li>
          );
        })
      }
    />
  );
};

export const AdminMultiSelectDropdown = ({
  ariaLabel,
  className,
  clearLabel,
  displayLabel,
  label,
  onChange,
  options,
  values,
}: AdminMultiSelectDropdownProps) => (
  <AdminDropdownShell
    ariaLabel={ariaLabel}
    className={className}
    displayLabel={displayLabel ?? (values.length ? `${label} (${values.length})` : label)}
    renderOptions={() => [
      ...options.map(option => {
        const checked = values.includes(option.id);
        return (
          <li key={option.id}>
            <label className={styles.adminDropdownMultiOption}>
              <span className={styles.adminDropdownOptionLabel}>
                {option.icon}
                {option.label}
              </span>
              <input
                type="checkbox"
                checked={checked}
                onChange={() =>
                  onChange(
                    checked ? values.filter(value => value !== option.id) : [...values, option.id]
                  )
                }
              />
            </label>
          </li>
        );
      }),
      clearLabel && values.length > 0 && (
        <li key="clear" className={styles.adminDropdownClear}>
          <button type="button" onClick={() => onChange([])}>
            <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
            {clearLabel}
          </button>
        </li>
      ),
    ]}
  />
);
